import {
  AlertCircle,
  ArrowUpRight,
  CalendarDays,
  CheckCircle2,
  Clock3,
  FileText,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import type { ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { motionTokens, riseItem, springs, staggerContainer } from "@/shared/lib/motion";

type MetricsLabels = {
  admissions: string;
  allInvoices: string;
  outOfPackageInvoices: string;
  inProgress: string;
  validated: string;
  savings: string;
  savingsRequested: string;
  savingsValidated: string;
  savingsGap: string;
  dailyAttendance: string;
};

type MetricsData = {
  admissions: { value: string; footer?: string };
  allInvoices: { value: string; amount: string };
  outOfPackageInvoices: { value: string; amount: string };
  inProgress: { value: string; amount: string };
  validated: { value: string; amount: string };
  savings: { value: string; footer: string; requested: string; validated: string };
};

type EnterpriseDashboardMetricsProps = {
  labels: MetricsLabels;
  data: MetricsData;
};

type FeatureTone = "green" | "blue" | "orange" | "red" | "mint" | "purple";

/** Grandes tuiles du tableau de bord Assurance (Safecheck-assureur), étendues à six teintes. */
const FEATURE_STYLES: Record<
  FeatureTone,
  { card: string; chip: string; label: string; value: string; sub: string; footer: string; dot: string; arrow: string }
> = {
  green: {
    card: "border-transparent bg-[linear-gradient(135deg,#0a4a31_0%,#0e6a40_50%,#15a34a_100%)] text-white",
    chip: "bg-accent text-accent-foreground",
    label: "text-white",
    value: "text-white",
    sub: "text-white/80",
    footer: "border-white/10 bg-black/15 text-white/90",
    dot: "bg-accent",
    arrow: "text-white/80",
  },
  blue: {
    card: "border-border bg-[linear-gradient(135deg,#ffffff_0%,#f4f8ff_55%,#e6effd_100%)] dark:bg-[linear-gradient(135deg,#132a2f_0%,#14283d_100%)]",
    chip: "bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300",
    label: "text-foreground",
    value: "text-foreground",
    sub: "text-muted-foreground",
    footer: "border-blue-100 bg-blue-50/80 text-muted-foreground dark:border-blue-500/10 dark:bg-blue-500/10",
    dot: "bg-blue-600",
    arrow: "text-muted-foreground",
  },
  orange: {
    card: "border-border bg-[linear-gradient(135deg,#ffffff_0%,#fff6ef_55%,#ffeadb_100%)] dark:bg-[linear-gradient(135deg,#132a2f_0%,#2e2219_100%)]",
    chip: "bg-orange-100 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300",
    label: "text-foreground",
    value: "text-foreground",
    sub: "text-muted-foreground",
    footer: "border-orange-100 bg-orange-50/80 text-muted-foreground dark:border-orange-500/10 dark:bg-orange-500/10",
    dot: "bg-orange-600",
    arrow: "text-muted-foreground",
  },
  red: {
    card: "border-border bg-[linear-gradient(135deg,#ffffff_0%,#fff4f4_55%,#fde2e2_100%)] dark:bg-[linear-gradient(135deg,#132a2f_0%,#3d1d20_100%)]",
    chip: "bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300",
    label: "text-foreground",
    value: "text-foreground",
    sub: "text-muted-foreground",
    footer: "border-red-100 bg-red-50/80 text-muted-foreground dark:border-red-500/10 dark:bg-red-500/10",
    dot: "bg-red-600",
    arrow: "text-muted-foreground",
  },
  mint: {
    card: "border-border bg-[linear-gradient(135deg,#ffffff_0%,#f0fbf5_55%,#dcf5e8_100%)] dark:bg-[linear-gradient(135deg,#132a2f_0%,#14382f_100%)]",
    chip: "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300",
    label: "text-foreground",
    value: "text-foreground",
    sub: "text-muted-foreground",
    footer: "border-emerald-100 bg-emerald-50/80 text-muted-foreground dark:border-emerald-500/10 dark:bg-emerald-500/10",
    dot: "bg-emerald-600",
    arrow: "text-muted-foreground",
  },
  purple: {
    card: "border-border bg-[linear-gradient(135deg,#ffffff_0%,#f7f3ff_55%,#ede5fd_100%)] dark:bg-[linear-gradient(135deg,#132a2f_0%,#2c2244_100%)]",
    chip: "bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300",
    label: "text-foreground",
    value: "text-foreground",
    sub: "text-muted-foreground",
    footer: "border-violet-100 bg-violet-50/80 text-muted-foreground dark:border-violet-500/10 dark:bg-violet-500/10",
    dot: "bg-violet-600",
    arrow: "text-muted-foreground",
  },
};

function FeatureCard({
  tone,
  icon: Icon,
  label,
  value,
  sub,
  footer,
  href,
}: {
  tone: FeatureTone;
  icon: LucideIcon;
  label: string;
  value: string;
  sub?: ReactNode;
  footer: ReactNode;
  href?: string;
}) {
  const styles = FEATURE_STYLES[tone];

  return (
    <motion.div
      variants={riseItem}
      whileHover={{ y: -motionTokens.distance.xs }}
      transition={springs.snappy}
      className={cn("relative isolate flex flex-col overflow-hidden rounded-[var(--radius-card)] border shadow-[var(--shadow-card)]", styles.card)}
    >
      {tone === "green" ? (
        // Reflets clairs en diagonale, comme la maquette.
        <span
          className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(115deg,transparent_45%,rgb(255_255_255/0.08)_55%,transparent_65%),radial-gradient(circle_at_85%_20%,rgb(166_242_27/0.25),transparent_45%)]"
          aria-hidden="true"
        />
      ) : null}
      <div className="flex flex-1 flex-col gap-4 p-5">
        <div className="flex items-center justify-between gap-3">
          <dt className={cn("flex items-center gap-3 text-[15px] font-semibold", styles.label)}>
            <span className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-xl", styles.chip)}>
              <Icon size={19} strokeWidth={2} aria-hidden="true" />
            </span>
            {label}
          </dt>
          {href ? <ArrowUpRight size={18} className={cn("shrink-0", styles.arrow)} aria-hidden="true" /> : null}
        </div>
        <dd className="min-w-0">
          <p className={cn("break-words text-[2rem] font-bold leading-none tracking-tight tabular-nums", styles.value)}>{value}</p>
          {sub ? <div className={cn("mt-2 text-sm", styles.sub)}>{sub}</div> : null}
        </dd>
      </div>
      <p className={cn("flex items-center gap-2 border-t px-5 py-3 text-xs font-medium", styles.footer)}>
        <span className={cn("h-2 w-2 shrink-0 rounded-full", styles.dot)} aria-hidden="true" />
        {footer}
      </p>
      {href ? (
        <Link
          to={href}
          aria-label={label}
          className="absolute inset-0 z-10 rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        />
      ) : null}
    </motion.div>
  );
}

export function EnterpriseDashboardMetrics({ labels, data }: EnterpriseDashboardMetricsProps) {
  return (
    <motion.dl
      className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
    >
      <FeatureCard
        tone="green"
        icon={CalendarDays}
        label={labels.admissions}
        value={data.admissions.value}
        footer={data.admissions.footer || labels.dailyAttendance}
        href="/dashboard/admissions"
      />
      <FeatureCard
        tone="blue"
        icon={FileText}
        label={labels.allInvoices}
        value={data.allInvoices.value}
        footer={data.allInvoices.amount}
        href="/dashboard/invoices"
      />
      <FeatureCard
        tone="red"
        icon={AlertCircle}
        label={labels.outOfPackageInvoices}
        value={data.outOfPackageInvoices.value}
        footer={data.outOfPackageInvoices.amount}
      />
      <FeatureCard
        tone="orange"
        icon={Clock3}
        label={labels.inProgress}
        value={data.inProgress.value}
        footer={data.inProgress.amount}
      />
      <FeatureCard
        tone="mint"
        icon={CheckCircle2}
        label={labels.validated}
        value={data.validated.value}
        footer={data.validated.amount}
        href="/dashboard/invoices/approved"
      />
      <FeatureCard
        tone="purple"
        icon={Zap}
        label={labels.savings}
        value={data.savings.value}
        sub={
          <span className="flex flex-col gap-0.5">
            <span className="flex justify-between gap-3">
              <span>{labels.savingsRequested}</span>
              <span className="font-semibold text-foreground tabular-nums">{data.savings.requested}</span>
            </span>
            <span className="flex justify-between gap-3">
              <span>{labels.savingsValidated}</span>
              <span className="font-semibold text-foreground tabular-nums">{data.savings.validated}</span>
            </span>
          </span>
        }
        footer={
          <span className="flex w-full justify-between gap-3">
            <span>{labels.savingsGap}</span>
            <span className="font-semibold tabular-nums text-foreground">{data.savings.footer}</span>
          </span>
        }
      />
    </motion.dl>
  );
}
